import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Group as GroupIcon,
    Person as PersonIcon,
    Star as StarIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Email as EmailIcon,
    Search as SearchIcon,
    Add as AddIcon,
    EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { groupService } from '../../services/groupService';
import { groupApplicationService } from '../../services/groupApplicationService';
import { groupTypeService } from '../../services/groupTypeService';
import { householdService } from '../../services/householdService';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';

const TeamSeekingTeams = ({ authData }) => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [teams, setTeams] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [allLeagues, setAllLeagues] = useState([]);
    const [myTeams, setMyTeams] = useState([]);
    const [groupTypes, setGroupTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
    const [browseTeamsDialogOpen, setBrowseTeamsDialogOpen] = useState(false);
    const [browseLeaguesDialogOpen, setBrowseLeaguesDialogOpen] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState('');
    const [applicationReason, setApplicationReason] = useState('');
    const [pendingApplications, setPendingApplications] = useState({});
    const [rejectedApplications, setRejectedApplications] = useState({});
    const [teamInvitations, setTeamInvitations] = useState([]);
    const [showApplicationInfo, setShowApplicationInfo] = useState(false);
    const [browseFilter, setBrowseFilter] = useState('seeking');
    const [householdDetails, setHouseholdDetails] = useState(null);

    // Review dialog states for team invitations
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [showReviewSuccessMessage, setShowReviewSuccessMessage] = useState(false);

    // Student selection dialog states
    const [studentSelectionDialogOpen, setStudentSelectionDialogOpen] = useState(false);
    const [selectedLeagueForEnrollment, setSelectedLeagueForEnrollment] = useState(null);
    const [selectedStudentForEnrollment, setSelectedStudentForEnrollment] = useState('');

    const loadTeamsSeekingMembers = useCallback(async () => {
        try {
            const data = await groupService.getGroupsSeekingMembersWithMembers();
            setTeams(data);
            setError(null);
        } catch (err) {
            setError('Failed to load teams seeking members: ' + err.message);
        }
    }, []);

    const loadAllTeams = useCallback(async () => {
        try {
            if (authData?.householdId) {
                const data = await groupService.getTeamsForHouseholdWithMembers(authData.householdId);
                setAllTeams(data);
            } else {
                setAllTeams([]);
            }
        } catch (err) {
            console.error('Failed to load all teams:', err);
        }
    }, [authData?.householdId]);

    const loadAllLeagues = useCallback(async () => {
        try {
            const data = await groupService.getAllGroups();
            const leagues = data.filter(group => isLeague(group));
            setAllLeagues(leagues);
        } catch (err) {
            console.error('Failed to load all leagues:', err);
        }
    }, []);

    const loadMyTeams = useCallback(async () => {
        try {
            if (authData?.householdId) {
                // Get all students in the household
                const householdDetail = await householdService.getHouseholdDetailById(authData.householdId);

                if (householdDetail && householdDetail.users && householdDetail.users.length > 0) {
                    // Get all roles to find the caregiver role ID
                    const allRoles = await roleService.getAllRoles();
                    const caregiverRole = allRoles.find(role => role.name === 'Caregiver');

                    if (!caregiverRole) {
                        setMyTeams([]);
                        return;
                    }

                    // Get detailed user information for all household members
                    const usersWithRoles = await Promise.all(
                        householdDetail.users.map(async (user) => {
                            try {
                                const userWithRoles = await userService.getUserById(user.id);
                                return userWithRoles;
                            } catch (err) {
                                console.error(`Failed to get user details for ${user.id}:`, err);
                                return user; // Return original user if role fetch fails
                            }
                        })
                    );

                    // Filter for students (users who are not caregivers)
                    const studentUsers = usersWithRoles.filter(user => {
                        if (!user.roleIds || user.roleIds.length === 0) {
                            return false; // Don't include users without role information
                        }

                        const isCaregiver = user.roleIds.includes(caregiverRole.id);
                        return !isCaregiver;
                    });

                    // Get student IDs
                    const studentIds = studentUsers.map(student => student.id);

                    // Get all groups and filter for those that have students from this household
                    const allGroups = await groupService.getAllGroups();

                    // Filter groups that have students from this household as members
                    const householdTeams = allGroups.filter(group => {
                        if (!group.userIds || group.userIds.length === 0) {
                            return false;
                        }

                        return studentIds.some(studentId => group.userIds.includes(studentId));
                    });

                    setMyTeams(householdTeams);
                    setStudents(studentUsers);
                } else {
                    setMyTeams([]);
                }
            } else {
                setMyTeams([]);
            }
        } catch (err) {
            console.error('Failed to load my teams:', err);
            setMyTeams([]);
        }
    }, [authData?.householdId]);

    const loadGroupTypes = useCallback(async () => {
        try {
            const data = await groupTypeService.getAllGroupTypes();
            setGroupTypes(data);
        } catch (err) {
            console.error('Failed to load group types:', err);
        }
    }, []);

    const loadStudents = useCallback(async () => {
        try {
            if (authData?.householdId) {
                // Try to get detailed household information
                const householdDetail = await householdService.getHouseholdDetailById(authData.householdId);

                if (householdDetail && householdDetail.users && householdDetail.users.length > 0) {
                    // First, get all roles to find the caregiver role ID
                    const allRoles = await roleService.getAllRoles();

                    const caregiverRole = allRoles.find(role => role.name === 'Caregiver');

                    if (!caregiverRole) {
                        setStudents([]);
                        return;
                    }

                    // Try to get role information for each user
                    const usersWithRoles = await Promise.all(
                        householdDetail.users.map(async (user) => {
                            try {
                                const userWithRoles = await userService.getUserById(user.id);
                                return userWithRoles;
                            } catch (err) {
                                return user; // Return original user if role fetch fails
                            }
                        })
                    );

                    // Filter for students (users who are not primary caregivers)
                    const studentUsers = usersWithRoles.filter(user => {
                        if (!user.roleIds || user.roleIds.length === 0) {
                            return false; // Don't include users without role information
                        }

                        const isCaregiver = user.roleIds.includes(caregiverRole.id);
                        return !isCaregiver;
                    });

                    setStudents(studentUsers);
                } else {
                    // Fallback: try to get the current user's information
                    if (authData?.userId) {
                        const currentUser = await userService.getUserById(authData.userId);
                        // For now, let's assume the current user is a student if they're not a caregiver
                        if (currentUser && currentUser.roleIds && !currentUser.roleIds.includes('caregiver')) {
                            setStudents([currentUser]);
                        } else {
                            setStudents([]);
                        }
                    } else {
                        setStudents([]);
                    }
                }
            } else {
                setStudents([]);
            }
        } catch (err) {
            console.error('Failed to load students:', err);
            setStudents([]);
        }
    }, [authData?.householdId, authData?.userId]);

    const loadPendingApplications = useCallback(async () => {
        try {
            const applications = await groupApplicationService.getApplicationsByHousehold(authData.householdId);
            const pendingMap = {};
            const rejectedMap = {};
            applications.forEach(app => {
                // Skip invitations - they are handled separately in loadTeamInvitations
                if (app.applicationType === 'INVITATION') {
                    return;
                }

                if (app.status === 'REJECTED') {
                    rejectedMap[app.groupId] = app; // Store the full application object
                } else if (app.status !== 'APPROVED') {
                    // Store multiple applications per team as an array
                    if (!pendingMap[app.groupId]) {
                        pendingMap[app.groupId] = [];
                    }
                    pendingMap[app.groupId].push(app);
                }
            });
            setPendingApplications(pendingMap);
            setRejectedApplications(rejectedMap);
        } catch (err) {
            console.error('Failed to load pending applications:', err);
        }
    }, [authData.householdId]);

    const loadTeamInvitations = useCallback(async () => {
        try {
            const invitations = await groupApplicationService.getTeamInvitationsForHousehold(authData.householdId);
            // Only show invitations that are ready for primary caregiver review
            // Don't show APPROVED invitations as they are completed and user is already in the team
            const pendingInvitations = invitations.filter(invitation =>
                invitation.status === 'COACH_STAFF_APPROVED' ||
                invitation.status === 'PRIMARY_CAREGIVER_APPROVED'
            );
            setTeamInvitations(pendingInvitations);
        } catch (err) {
            console.error('Failed to load team invitations:', err);
        }
    }, [authData.householdId]);

    const loadHouseholdDetails = useCallback(async () => {
        try {
            if (authData?.householdId) {
                const details = await householdService.getHouseholdDetailById(authData.householdId);
                console.log('Household details:', details);
                console.log('Current user ID:', authData.userId);
                console.log('Primary caregiver ID:', details.primaryCaregiverId);
                setHouseholdDetails(details);
            }
        } catch (err) {
            console.error('Failed to load household details:', err);
        }
    }, [authData?.householdId, authData?.userId]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                loadTeamsSeekingMembers(),
                loadAllTeams(),
                loadAllLeagues(),
                loadMyTeams(),
                loadGroupTypes(),
                loadStudents(),
                loadPendingApplications(),
                loadTeamInvitations(),
                loadHouseholdDetails()
            ]);
            setLoading(false);
        };
        loadData();
    }, [loadPendingApplications, loadTeamInvitations, loadTeamsSeekingMembers, loadAllTeams, loadMyTeams, loadGroupTypes, loadStudents, loadHouseholdDetails]);

    const handleSubmitApplication = async () => {
        try {
            if (!selectedStudent || !applicationReason.trim()) {
                setError('Please select a student and provide a reason for applying.');
                return;
            }

            const student = students.find(s => s.id.toString() === selectedStudent);
            if (!student) {
                setError('Selected student not found.');
                return;
            }

            await groupApplicationService.submitApplication(
                student.id,
                selectedTeam.id,
                authData.householdId,
                applicationReason
            );

            setApplicationDialogOpen(false);
            setError(null);
            setShowApplicationInfo(true);
            // Reload pending applications to update the UI
            loadPendingApplications();
        } catch (err) {
            setError('Failed to submit application: ' + err.message);
        }
    };

    const handleReviewInvitation = (invitation) => {
        if (!isCurrentUserPrimaryCaregiver()) {
            setError('Only the primary caregiver can review team invitations.');
            return;
        }
        setSelectedInvitation(invitation);
        setReviewNotes('');
        setReviewDialogOpen(true);
    };

    const handleEnrollInLeague = async (league) => {
        try {
            console.log('Preparing to enroll in league:', league);
            console.log('Students available:', students);
            console.log('Current user ID:', authData.userId);
            console.log('Household details:', householdDetails);
            
            // Check if there's a primary caregiver set
            if (!hasPrimaryCaregiver()) {
                setError('No primary caregiver is set for this household. Please set a primary caregiver in the Household Dashboard before enrolling in leagues.');
                return;
            }

            // Double-check that the current user is the primary caregiver
            if (!isCurrentUserPrimaryCaregiver()) {
                setError('Only the primary caregiver can enroll students in leagues. Please contact the primary caregiver to enroll in this league.');
                return;
            }



            // Check if we have students to enroll
            const availableStudents = getAvailableStudentsForLeagueEnrollment();
            if (availableStudents.length === 0) {
                setError('No students available for enrollment. All students in this household are already enrolled in leagues.');
                return;
            }

            // Always open selection dialog so user knows which student is being enrolled
            setSelectedLeagueForEnrollment(league);
            // Pre-select the student if there's only one available
            setSelectedStudentForEnrollment(availableStudents.length === 1 ? availableStudents[0].id.toString() : '');
            setStudentSelectionDialogOpen(true);

        } catch (err) {
            console.error('Enrollment error:', err);
            setError('Failed to enroll in league: ' + err.message);
        }
    };

    const enrollStudentInLeague = async (student, league) => {
        try {
            // TEMPORARY: Check if this specific student is already enrolled in any league
            const studentEnrolledLeagues = allLeagues.filter(l => {
                if (!l.userIds || l.userIds.length === 0) return false;
                return l.userIds.includes(student.id);
            });
            
            console.log('Leagues this student is enrolled in:', studentEnrolledLeagues);
            
            if (studentEnrolledLeagues.length > 0) {
                const leagueNames = studentEnrolledLeagues.map(l => l.name);
                setError(`Student ${getDisplayName(student)} is already enrolled in league(s): ${leagueNames.join(', ')}. Temporarily, students can only be enrolled in one league at a time.`);
                return;
            }

            console.log('Enrolling student:', student, 'in league:', league.id);
            console.log('API call parameters:', {
                userId: student.id,
                groupId: league.id,
                requestedBy: authData.userId
            });
            
            // Add the student to the league
            const result = await groupService.addUserToGroup(student.id, league.id, authData.userId);
            console.log('Enrollment result:', result);

            setSuccess(`Successfully enrolled ${getDisplayName(student)} in ${league.name}. Redirecting to set their availability...`);
            
            // Close dialog if it was open
            setStudentSelectionDialogOpen(false);
            setSelectedLeagueForEnrollment(null);
            setSelectedStudentForEnrollment('');
            
            // Reload data to update the UI
            await Promise.all([
                loadMyTeams(), // Reload user's teams to show the new enrollment
                loadAllLeagues() // Reload leagues to update member counts
            ]);
            
            // Navigate to household dashboard to open availability dialog for the enrolled student
            setTimeout(() => {
                navigate(`/household/dashboard?openAvailability=true&studentId=${student.id}`);
            }, 2000); // Give user time to see the success message

        } catch (err) {
            console.error('Enrollment error:', err);
            setError('Failed to enroll in league: ' + err.message);
        }
    };

    const handleSubmitReview = async (approved) => {
        try {
            // Check if there's a primary caregiver set
            if (!hasPrimaryCaregiver()) {
                setError('No primary caregiver is set for this household. Please set a primary caregiver in the Household Dashboard before reviewing team invitations.');
                return;
            }

            // Double-check that the current user is the primary caregiver
            if (!isCurrentUserPrimaryCaregiver()) {
                setError('Only the primary caregiver can review team invitations. Please contact the primary caregiver to review this invitation.');
                return;
            }

            await groupApplicationService.reviewByPrimaryCaregiver(
                selectedInvitation.id,
                authData.userId,
                approved,
                reviewNotes
            );

            setReviewDialogOpen(false);
            setShowReviewSuccessMessage(true);

            // Reload data to update the UI after approval
            await Promise.all([
                loadTeamInvitations(), // Reload invitations
                loadMyTeams(), // Reload user's teams in case they were added
                loadPendingApplications() // Reload applications
            ]);

            // Show appropriate success message
            if (approved) {
                setSuccess(`Invitation approved! ${selectedInvitation.userDisplayName} has been added to ${selectedInvitation.groupName}.`);
            } else {
                setSuccess('Invitation rejected successfully.');
            }

            // Hide success message after 5 seconds
            setTimeout(() => {
                setShowReviewSuccessMessage(false);
                setSuccess('');
            }, 5000);
        } catch (err) {
            console.error('Review submission error:', err);
            if (err.message.includes('Only the primary caregiver can review this invitation')) {
                setError('You are not the primary caregiver for this household. Only the primary caregiver can review team invitations. Please set a primary caregiver in the Household Dashboard.');
            } else {
                setError('Failed to submit review: ' + err.message);
            }
        }
    };

    const getDisplayName = (user) => {
        const name = user.preferredName || user.firstName;
        const lastNameInitial = user.lastName ? user.lastName.substring(0, 1) : '';
        const graduationYear = user.graduationYear || '';
        return `${name} ${lastNameInitial}. ${graduationYear}`;
    };

    const getGroupTypeName = (groupTypeId, groupTypeName) => {
        // If groupTypeName is already available in the team data, use it
        if (groupTypeName) {
            return groupTypeName;
        }

        // Handle undefined or null groupTypeId
        if (!groupTypeId) {
            console.error('GroupTypeId is undefined/null:', { groupTypeId });
            return 'Unknown Type';
        }

        // Convert to number for comparison since IDs might be strings
        const numericId = Number(groupTypeId);
        const groupType = groupTypes.find(gt => gt.id === numericId);

        // Debug logging
        if (!groupType) {
            console.error('GroupType not found:', {
                groupTypeId,
                numericId,
                availableGroupTypes: groupTypes.map(gt => ({ id: gt.id, name: gt.name }))
            });
        }

        return groupType ? groupType.name : 'Unknown Type';
    };

    const isTeamInStudentLeague = (team) => {
        // Check if any student in the household is in the same league as this team
        const result = students.some(student => {
            // Check if the student is not already in a team of the same type
            const studentAlreadyInTeam = student.groupMemberships && student.groupMemberships.some(membership => {
                // Use groupTypeName if available, otherwise fall back to groupTypeId comparison
                if (team.groupTypeName && membership.groupTypeName) {
                    return membership.groupTypeName === team.groupTypeName;
                }
                const isSameType = membership.groupTypeId === team.groupTypeId;
                return isSameType;
            });

            // For now, allow application if student is not already in a team of the same type
            // This is a simplified approach - in a real system you'd check league membership more specifically
            const canApply = !studentAlreadyInTeam;
            return canApply;
        });

        return result;
    };

    const hasPendingApplication = (teamId) => {
        const applications = pendingApplications[teamId];
        if (!applications || applications.length === 0) return false;
        // Only consider applications from students in the current household
        const householdStudentIds = students.map(student => student.id);
        return applications.some(app => householdStudentIds.includes(app.userId));
    };

    const hasPendingApplicationForStudent = (teamId, studentId) => {
        const applications = pendingApplications[teamId];
        if (!applications || applications.length === 0) return false;
        return applications.some(app => app.userId === studentId);
    };

    const hasRejectedApplication = (teamId) => {
        const rejectedApp = rejectedApplications[teamId];
        return rejectedApp || false;
    };

    const isCurrentUserPrimaryCaregiver = () => {
        return householdDetails?.primaryCaregiverId === authData?.userId;
    };

    const hasPrimaryCaregiver = () => {
        return householdDetails?.primaryCaregiverId !== null && householdDetails?.primaryCaregiverId !== undefined;
    };

    const getStudentsInTeam = (teamId) => {
        const team = myTeams.find(t => t.id === teamId);
        if (!team || !team.userIds) {
            return [];
        }

        return students.filter(student =>
            team.userIds.includes(student.id)
        );
    };

    const getStudentsInLeague = (leagueId) => {
        const league = myTeams.find(l => l.id === leagueId);
        if (!league || !league.userIds) {
            return [];
        }

        return students.filter(student =>
            league.userIds.includes(student.id)
        );
    };

    const isLeague = (team) => {
        // Leagues have group types that start with "League-"
        return team.groupTypeName && team.groupTypeName.startsWith('League-');
    };

    const isTeam = (team) => {
        // Teams have group types that match "FTC-*-Team"
        return team.groupTypeName && team.groupTypeName.match(/^FTC-.*-Team$/);
    };

    const getEnrolledLeagues = () => {
        return myTeams.filter(isLeague);
    };

    const getEnrolledTeams = () => {
        return myTeams.filter(isTeam);
    };

    const getFilteredTeams = () => {
        // Filter teams to only include those with FTC-*-Team naming pattern
        const ftcTeams = allTeams.filter(team => {
            // Use groupTypeName if available, otherwise fall back to lookup
            if (team.groupTypeName) {
                return /^FTC-.*-Team$/.test(team.groupTypeName);
            }

            if (!team.groupTypeId) {
                console.error('Team missing groupTypeId:', team);
                return false;
            }
            const numericId = Number(team.groupTypeId);
            const groupType = groupTypes.find(gt => gt.id === numericId);
            return groupType && /^FTC-.*-Team$/.test(groupType.name);
        });

        if (browseFilter === 'seeking') {
            return teams.filter(team => {
                // Use groupTypeName if available, otherwise fall back to lookup
                if (team.groupTypeName) {
                    return /^FTC-.*-Team$/.test(team.groupTypeName);
                }

                if (!team.groupTypeId) {
                    console.error('Team seeking members missing groupTypeId:', team);
                    return false;
                }
                const numericId = Number(team.groupTypeId);
                const groupType = groupTypes.find(gt => gt.id === numericId);
                return groupType && /^FTC-.*-Team$/.test(groupType.name);
            });
        } else {
            return ftcTeams.filter(team => team.isActive);
        }
    };

    const getAvailableStudentsForLeagueEnrollment = () => {
        // Filter out students who are already enrolled in any league
        return students.filter(student => {
            // Check if student is enrolled in any league
            const isEnrolledInLeague = allLeagues.some(league => {
                if (!league.userIds || league.userIds.length === 0) return false;
                return league.userIds.includes(student.id);
            });
            
            return !isEnrolledInLeague;
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }



    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary">
                Enrolled Programs
            </Typography>

            {/* Success and Error Alerts */}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Application Info Message */}
            {showApplicationInfo && selectedTeam && (
                <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    onClose={() => setShowApplicationInfo(false)}
                >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Application Submitted for {selectedTeam.name}</strong>
                    </Typography>
                    <Typography variant="body2">
                        Your application has been submitted and is pending review. Both the team supervisor and a coach or staff member
                        will review your application independently. You will be notified once both reviews are complete and a final decision has been made.
                    </Typography>
                </Alert>
            )}

            {/* Enrolled Leagues Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h6" color="primary">
                                    Enrolled Leagues
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onClick={() => setBrowseLeaguesDialogOpen(true)}
                                >
                                    Browse Leagues
                                </Button>
                            </Box>
                            


                            {getEnrolledLeagues().length > 0 ? (
                                <Grid container spacing={2}>
                                    {getEnrolledLeagues().map((league) => (
                                        <Grid item xs={12} md={6} lg={4} key={`league-${league.id}`}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <TrophyIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant="h6" component="h3">
                                                                {league.name}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label="Member"
                                                            color="success"
                                                            size="small"
                                                            icon={<CheckCircleIcon />}
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </Box>

                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        {league.description || 'No description available'}
                                                    </Typography>

                                                    <Chip
                                                        label={getGroupTypeName(league.groupTypeId, league.groupTypeName)}
                                                        size="small"
                                                        sx={{ mb: 2 }}
                                                    />

                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        Household Students in League:
                                                    </Typography>

                                                    <List dense sx={{ mb: 2 }}>
                                                        {getStudentsInTeam(league.id).map((student) => (
                                                            <ListItem key={student.id} sx={{ py: 0.5 }}>
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    <PersonIcon fontSize="small" />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={getDisplayName(student)}
                                                                    secondary="Student"
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                        {getStudentsInTeam(league.id).length === 0 && (
                                                            <ListItem>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No household students in this league
                                                                </Typography>
                                                            </ListItem>
                                                        )}
                                                    </List>

                                                    <Divider sx={{ my: 2 }} />

                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {getStudentsInTeam(league.id).length} household student(s) in league
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <TrophyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No Enrolled Leagues
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        You haven't enrolled in any leagues yet. Use the "Browse Leagues" button above to find and join leagues.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

                         {/* Enrolled Teams Section */}
             <Grid container spacing={3} sx={{ mb: 4 }}>
                 <Grid item xs={12}>
                     <Card>
                         <CardContent>
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                 <Typography variant="h6" color="primary">
                                     Enrolled Teams
                                 </Typography>
                                 <Button
                                     variant="outlined"
                                     size="small"
                                     startIcon={<SearchIcon />}
                                     onClick={() => setBrowseTeamsDialogOpen(true)}
                                 >
                                     Browse Teams
                                 </Button>
                             </Box>
                             
                             {/* Combine enrolled teams and teams with pending applications */}
                             {(() => {
                                 const enrolledTeams = getEnrolledTeams();
                                 const teamsWithPendingApps = Object.keys(pendingApplications).map(teamId => {
                                     const team = teams.find(t => t.id.toString() === teamId) || allTeams.find(t => t.id.toString() === teamId);
                                     const applications = pendingApplications[teamId];
                                     // Filter applications to only show those from students in the current household
                                     const householdStudentIds = students.map(student => student.id);
                                     const householdApplications = applications ? applications.filter(app => householdStudentIds.includes(app.userId)) : [];
                                     return { team, applications: householdApplications, isPending: true };
                                 }).filter(item => item.team && item.applications && item.applications.length > 0);
                                 
                                 const allTeamsToShow = [...enrolledTeams.map(team => ({ team, isPending: false })), ...teamsWithPendingApps];
                                 
                                 if (allTeamsToShow.length > 0) {
                                     return (
                                         <Grid container spacing={2}>
                                             {allTeamsToShow.map(({ team, applications, isPending }) => (
                                                 <Grid item xs={12} md={6} lg={4} key={`${isPending ? 'pending' : 'enrolled'}-${team.id}`}>
                                                     <Card variant="outlined" sx={{ height: '100%' }}>
                                                         <CardContent>
                                                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                     <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                                     <Box>
                                                                         <Typography variant="h6" component="h3">
                                                                             {team.name}
                                                                         </Typography>
                                                                         {team.code && (
                                                                             <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium' }}>
                                                                                 Team #{team.code}
                                                                             </Typography>
                                                                         )}
                                                                     </Box>
                                                                 </Box>
                                                                 <Chip
                                                                     label={isPending ? "Application Pending" : "Member"}
                                                                     color={isPending ? "warning" : "success"}
                                                                     size="small"
                                                                     icon={<CheckCircleIcon />}
                                                                     sx={{ ml: 2 }}
                                                                 />
                                                             </Box>

                                                             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                 {team.description || 'No description available'}
                                                             </Typography>

                                                             <Chip
                                                                 label={getGroupTypeName(team.groupTypeId, team.groupTypeName)}
                                                                 size="small"
                                                                 sx={{ mb: 2 }}
                                                             />

                                                             {isPending && applications && applications.length > 0 && (
                                                                 <Box sx={{ mb: 2 }}>
                                                                     <Typography variant="subtitle2" color="warning.main" sx={{ mb: 0.5 }}>
                                                                         Students Applying:
                                                                     </Typography>
                                                                     {applications.map((application, index) => (
                                                                         <Typography key={application.id} variant="body2" color="warning.main">
                                                                             {application.userDisplayName || application.userName}
                                                                         </Typography>
                                                                     ))}
                                                                 </Box>
                                                             )}

                                                             <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                                 {isPending ? 'Current Members:' : 'Household Students in Team:'}
                                                             </Typography>

                                                             <List dense sx={{ mb: 2 }}>
                                                                 {isPending ? (
                                                                     // Show current team members for pending applications
                                                                     <>
                                                                         {team.members && team.members.slice(0, 3).map((member) => (
                                                                             <ListItem key={member.id} sx={{ py: 0.5 }}>
                                                                                 <ListItemIcon sx={{ minWidth: 32 }}>
                                                                                     {member.isPrimaryUser ? (
                                                                                         <StarIcon color="primary" fontSize="small" />
                                                                                     ) : (
                                                                                         <PersonIcon fontSize="small" />
                                                                                     )}
                                                                                 </ListItemIcon>
                                                                                 <ListItemText
                                                                                     primary={member.name}
                                                                                     secondary={member.isPrimaryUser ? 'Team Supervisor' : 'Member'}
                                                                                     primaryTypographyProps={{ variant: 'body2' }}
                                                                                     secondaryTypographyProps={{ variant: 'caption' }}
                                                                                 />
                                                                             </ListItem>
                                                                         ))}
                                                                         {team.members && team.members.length > 3 && (
                                                                             <ListItem>
                                                                                 <Typography variant="caption" color="text.secondary">
                                                                                     +{team.members.length - 3} more members
                                                                                 </Typography>
                                                                             </ListItem>
                                                                         )}
                                                                     </>
                                                                 ) : (
                                                                     // Show household students for enrolled teams
                                                                     <>
                                                                         {getStudentsInTeam(team.id).map((student) => (
                                                                             <ListItem key={student.id} sx={{ py: 0.5 }}>
                                                                                 <ListItemIcon sx={{ minWidth: 32 }}>
                                                                                     <PersonIcon fontSize="small" />
                                                                                 </ListItemIcon>
                                                                                 <ListItemText
                                                                                     primary={getDisplayName(student)}
                                                                                     secondary="Student"
                                                                                     primaryTypographyProps={{ variant: 'body2' }}
                                                                                     secondaryTypographyProps={{ variant: 'caption' }}
                                                                                 />
                                                                             </ListItem>
                                                                         ))}
                                                                         {getStudentsInTeam(team.id).length === 0 && (
                                                                             <ListItem>
                                                                                 <Typography variant="caption" color="text.secondary">
                                                                                     No household students in this team
                                                                                 </Typography>
                                                                             </ListItem>
                                                                         )}
                                                                     </>
                                                                 )}
                                                             </List>

                                                             <Divider sx={{ my: 2 }} />

                                                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                 <Typography variant="caption" color="text.secondary">
                                                                     {isPending ? 
                                                                         `${team.members ? team.members.length : 0} members` : 
                                                                         `${getStudentsInTeam(team.id).length} household student(s) in team`
                                                                     }
                                                                 </Typography>
                                                                 {isPending && (
                                                                     <Typography variant="caption" color="warning.main">
                                                                         Application under review
                                                                     </Typography>
                                                                 )}
                                                             </Box>
                                                         </CardContent>
                                                     </Card>
                                                 </Grid>
                                             ))}
                                         </Grid>
                                     );
                                 } else {
                                     return (
                                         <Box sx={{ textAlign: 'center', py: 4 }}>
                                             <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                             <Typography variant="h6" color="text.secondary" gutterBottom>
                                                 No Enrolled Teams
                                             </Typography>
                                             <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                 You haven't enrolled in any teams yet. Use the "Browse Teams" button above to find and join teams.
                                             </Typography>
                                         </Box>
                                     );
                                 }
                             })()}
                         </CardContent>
                     </Card>
                 </Grid>
             </Grid>

            {/* Empty State */}
            {myTeams.length === 0 && teamInvitations.length === 0 && Object.keys(pendingApplications).length === 0 && Object.keys(rejectedApplications).length === 0 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    No students in this household are currently members of any teams and there are no pending applications or invitations.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Team Invitations */}
            {teamInvitations.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: 'warning.main' }}>
                        Team Invitations
                    </Typography>
                    <Grid container spacing={2}>
                        {teamInvitations.map((invitation) => (
                            <Grid item xs={12} md={6} lg={4} key={`invitation-${invitation.id}`}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <EmailIcon sx={{ mr: 1, color: 'warning.main' }} />
                                                <Typography variant="h6" component="h3">
                                                    {invitation.groupName}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={invitation.status === 'COACH_STAFF_APPROVED' ? 'Ready for Review' : 'Pending Final Approval'}
                                                color="warning"
                                                size="small"
                                                icon={<EmailIcon />}
                                                sx={{ ml: 2 }}
                                            />
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            <strong>Student:</strong> {invitation.userDisplayName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            <strong>Team Supervisor:</strong> {invitation.teamSupervisorReviewedByName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            <strong>Reason:</strong> {invitation.reason || 'No reason provided'}
                                        </Typography>

                                        <Chip
                                            label={getGroupTypeName(invitation.groupTypeId, invitation.groupTypeName)}
                                            size="small"
                                            sx={{ mb: 2 }}
                                        />

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Invitation from {invitation.teamSupervisorReviewedByName}
                                            </Typography>
                                            {!hasPrimaryCaregiver() ? (
                                                <Typography variant="caption" color="error">
                                                    No primary caregiver set. Please set a primary caregiver in Household Dashboard.
                                                </Typography>
                                            ) : isCurrentUserPrimaryCaregiver() ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleReviewInvitation(invitation)}
                                                >
                                                    Review
                                                </Button>
                                            ) : (
                                                <Typography variant="caption" color="warning.main">
                                                    Waiting for primary caregiver review
                                                </Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}



            {/* Rejected Applications */}
            {Object.keys(rejectedApplications).length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
                        Rejected Applications
                    </Typography>
                    <Grid container spacing={2}>
                        {Object.keys(rejectedApplications).map((teamId) => {
                            const team = teams.find(t => t.id.toString() === teamId) || allTeams.find(t => t.id.toString() === teamId);
                            if (!team) return null;

                            const rejectedApp = rejectedApplications[teamId];
                            const rejectionReason = rejectedApp.teamSupervisorNotes || rejectedApp.coachStaffNotes || 'No reason provided';

                            return (
                                <Grid item xs={12} md={6} lg={4} key={`rejected-${teamId}`}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                    <Box>
                                                        <Typography variant="h6" component="h3">
                                                            {team.name}
                                                        </Typography>
                                                        {team.code && (
                                                            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium' }}>
                                                                Team #{team.code}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label="Application Rejected"
                                                    color="error"
                                                    size="small"
                                                    icon={<CancelIcon />}
                                                    sx={{ ml: 2 }}
                                                />
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {team.description || 'No description available'}
                                            </Typography>

                                            <Chip
                                                label={getGroupTypeName(team.groupTypeId, team.groupTypeName)}
                                                size="small"
                                                sx={{ mb: 2 }}
                                            />

                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Current Members:
                                            </Typography>

                                            <List dense sx={{ mb: 2 }}>
                                                {team.members && team.members.slice(0, 3).map((member) => (
                                                    <ListItem key={member.id} sx={{ py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            {member.isPrimaryUser ? (
                                                                <StarIcon color="primary" fontSize="small" />
                                                            ) : (
                                                                <PersonIcon fontSize="small" />
                                                            )}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={member.name}
                                                            secondary={member.isPrimaryUser ? 'Team Supervisor' : 'Member'}
                                                            primaryTypographyProps={{ variant: 'body2' }}
                                                            secondaryTypographyProps={{ variant: 'caption' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {team.members && team.members.length > 3 && (
                                                    <ListItem>
                                                        <Typography variant="caption" color="text.secondary">
                                                            +{team.members.length - 3} more members
                                                        </Typography>
                                                    </ListItem>
                                                )}
                                            </List>

                                            <Divider sx={{ my: 2 }} />

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {team.members ? team.members.length : 0} members
                                                </Typography>
                                            </Box>

                                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                                <strong>Rejection Reason:</strong> {rejectionReason}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}



            {/* Review Success Message */}
            {showReviewSuccessMessage && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => setShowReviewSuccessMessage(false)}>
                    Review submitted successfully!
                </Alert>
            )}



            {/* Application Dialog */}
            <Dialog open={applicationDialogOpen} onClose={() => setApplicationDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Apply to {selectedTeam?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Submit an application for a student in your household to join this team.
                        </Typography>

                        {(() => {
                            const availableStudents = students.filter(student => !hasPendingApplicationForStudent(selectedTeam?.id, student.id));
                            return (
                                <>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Select Student"
                                        value={selectedStudent}
                                        onChange={(e) => setSelectedStudent(e.target.value)}
                                        sx={{ mb: 3 }}
                                        disabled={availableStudents.length === 0}
                                    >
                                        {availableStudents.map((student) => (
                                            <MenuItem key={student.id} value={student.id.toString()}>
                                                {getDisplayName(student)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    {availableStudents.length === 0 && (
                                        <Alert severity="info" sx={{ mb: 3 }}>
                                            All students in this household already have pending applications for this team.
                                        </Alert>
                                    )}
                                </>
                            );
                        })()}

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Reason for Applying"
                            value={applicationReason}
                            onChange={(e) => setApplicationReason(e.target.value)}
                            placeholder="Please explain why this student would be a good fit for the team..."
                            helperText="Be specific about the student's skills, experience, and motivation."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplicationDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmitApplication}
                        variant="contained"
                        disabled={!selectedStudent || !applicationReason.trim() || students.filter(student => !hasPendingApplicationForStudent(selectedTeam?.id, student.id)).length === 0}
                    >
                        Submit Application
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Browse Teams Dialog */}
            <Dialog open={browseTeamsDialogOpen} onClose={() => setBrowseTeamsDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Browse Teams
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                Browse and apply to teams. You can filter between teams seeking members and all active teams.
                            </Typography>
                            <ToggleButtonGroup
                                value={browseFilter}
                                exclusive
                                onChange={(e, newFilter) => newFilter && setBrowseFilter(newFilter)}
                                size="small"
                            >
                                <ToggleButton value="seeking">Teams Seeking Members</ToggleButton>
                                <ToggleButton value="all">Show All Teams</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Grid container spacing={2}>
                            {getFilteredTeams().map((team) => (
                                <Grid item xs={12} md={6} key={team.id}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                <Box>
                                                    <Typography variant="h6" component="h3">
                                                        {team.name}
                                                    </Typography>
                                                    {team.code && (
                                                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium' }}>
                                                            Team #{team.code}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {team.description || 'No description available'}
                                            </Typography>

                                            <Chip
                                                label={getGroupTypeName(team.groupTypeId, team.groupTypeName)}
                                                size="small"
                                                sx={{ mb: 2 }}
                                            />

                                            {team.members && team.members.length > 0 ? (
                                                <>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        Current Members:
                                                    </Typography>

                                                    <List dense sx={{ mb: 2 }}>
                                                        {team.members.slice(0, 3).map((member) => (
                                                            <ListItem key={member.id} sx={{ py: 0.5 }}>
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    {member.isPrimaryUser ? (
                                                                        <StarIcon color="primary" fontSize="small" />
                                                                    ) : (
                                                                        <PersonIcon fontSize="small" />
                                                                    )}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={member.name}
                                                                    secondary={member.isPrimaryUser ? 'Team Supervisor' : 'Member'}
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                        {team.members.length > 3 && (
                                                            <ListItem>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    +{team.members.length - 3} more members
                                                                </Typography>
                                                            </ListItem>
                                                        )}
                                                    </List>

                                                    <Divider sx={{ my: 2 }} />
                                                </>
                                            ) : null}

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {team.members ? team.members.length : 0} members
                                                </Typography>

                                                {(() => {
                                                    const hasPending = hasPendingApplication(team.id);
                                                    const hasRejected = hasRejectedApplication(team.id);
                                                    const canApply = isTeamInStudentLeague(team);

                                                    if (hasRejected) {
                                                        const rejectedApp = rejectedApplications[team.id];
                                                        const rejectionReason = rejectedApp.teamSupervisorNotes || rejectedApp.coachStaffNotes || 'No reason provided';
                                                        return (
                                                            <Box>
                                                                <Chip
                                                                    label="Application Rejected"
                                                                    color="error"
                                                                    size="small"
                                                                    icon={<CancelIcon />}
                                                                    sx={{ mb: 1 }}
                                                                />
                                                                <Typography variant="caption" color="error" display="block">
                                                                    <strong>Reason:</strong> {rejectionReason}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    } else {
                                                        // Always show Apply button if team is in student's league, regardless of pending applications
                                                        // The application dialog will filter out students who already have pending applications
                                                        return (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {hasPending && (() => {
                                                                    const applications = pendingApplications[team.id];
                                                                    const householdStudentIds = students.map(student => student.id);
                                                                    const householdApplications = applications ? applications.filter(app => householdStudentIds.includes(app.userId)) : [];
                                                                    const count = householdApplications.length;
                                                                    return count > 0 ? (
                                                                        <Chip
                                                                            label={`${count} Application${count !== 1 ? 's' : ''} Pending`}
                                                                            color="warning"
                                                                            size="small"
                                                                            icon={<CheckCircleIcon />}
                                                                        />
                                                                    ) : null;
                                                                })()}
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    startIcon={<AddIcon />}
                                                                    onClick={() => {
                                                                        setSelectedTeam(team);
                                                                        setSelectedStudent('');
                                                                        setApplicationReason('');
                                                                        setBrowseTeamsDialogOpen(false);
                                                                        setApplicationDialogOpen(true);
                                                                    }}
                                                                    disabled={!canApply}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </Box>
                                                        );
                                                    }
                                                })()}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {getFilteredTeams().length === 0 && (
                            <Alert severity="info">
                                {browseFilter === 'seeking'
                                    ? 'No teams are currently seeking members.'
                                    : 'No active teams available.'}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBrowseTeamsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Browse Leagues Dialog */}
            <Dialog open={browseLeaguesDialogOpen} onClose={() => setBrowseLeaguesDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Browse Leagues
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Browse available leagues. You can view all active leagues and their current members.
                        </Typography>



                        <Grid container spacing={2}>
                            {allLeagues.map((league) => (
                                <Grid item xs={12} md={6} key={league.id}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <TrophyIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                <Typography variant="h6" component="h3">
                                                    {league.name}
                                                </Typography>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {league.description || 'No description available'}
                                            </Typography>

                                            <Chip
                                                label={getGroupTypeName(league.groupTypeId, league.groupTypeName)}
                                                size="small"
                                                sx={{ mb: 2 }}
                                            />

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {league.userIds ? league.userIds.length : 0} members
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleEnrollInLeague(league)}
                                                >
                                                    Enroll
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {allLeagues.length === 0 && (
                            <Alert severity="info">
                                No leagues are currently available.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBrowseLeaguesDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Student Selection Dialog for League Enrollment */}
            <Dialog open={studentSelectionDialogOpen} onClose={() => setStudentSelectionDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Select Student to Enroll
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Select which student to enroll in {selectedLeagueForEnrollment?.name}.
                        </Typography>

                        {getAvailableStudentsForLeagueEnrollment().length > 0 ? (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Select Student</InputLabel>
                                <Select
                                    value={selectedStudentForEnrollment}
                                    onChange={(e) => setSelectedStudentForEnrollment(e.target.value)}
                                    label="Select Student"
                                >
                                    {getAvailableStudentsForLeagueEnrollment().map((student) => (
                                        <MenuItem key={student.id} value={student.id}>
                                            {getDisplayName(student)}
                                            {student.school && ` - ${student.school}`}
                                            {student.graduationYear && ` (Class of ${student.graduationYear})`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                All students in this household are already enrolled in leagues.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStudentSelectionDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (selectedStudentForEnrollment && selectedLeagueForEnrollment) {
                                const student = getAvailableStudentsForLeagueEnrollment().find(s => s.id.toString() === selectedStudentForEnrollment);
                                if (student) {
                                    enrollStudentInLeague(student, selectedLeagueForEnrollment);
                                }
                            }
                        }}
                        variant="contained"
                        disabled={!selectedStudentForEnrollment || getAvailableStudentsForLeagueEnrollment().length === 0}
                    >
                        Enroll Student
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Dialog for Team Invitations */}
            <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Review Team Invitation
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Review the invitation for {selectedInvitation?.userDisplayName} to join {selectedInvitation?.groupName}.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Reason for invitation:</strong> {selectedInvitation?.reason || 'No reason provided'}
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Review Notes (Optional)"
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add any notes about your decision..."
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleSubmitReview(false)}
                        color="error"
                        variant="outlined"
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => handleSubmitReview(true)}
                        variant="contained"
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TeamSeekingTeams; 